/**
 * SMS character + segment counting for the Chat composer's live counter.
 *
 * Carriers bill per 160-character segment for plain GSM-7 text, or per
 * 70-character segment when the body contains any non-GSM character (emoji,
 * curly quotes, etc.), which forces UCS-2 encoding. Multi-segment messages
 * carry a small user-data header, dropping the per-segment budget to 153
 * (GSM-7) / 67 (UCS-2). This mirrors Twilio's own segmentation so the count we
 * show matches what the merchant is billed.
 */

export type SmsEncoding = "GSM-7" | "UCS-2";

export interface SmsSegmentInfo {
  /** UTF-16 code units the counter displays (chars as the user perceives most). */
  length: number;
  segments: number;
  encoding: SmsEncoding;
  /** Characters remaining in the current segment. */
  remainingInSegment: number;
}

// The GSM 03.38 basic character set plus its extension characters (each
// extension char takes two GSM-7 code units).
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€";

function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (GSM_BASIC.indexOf(ch) === -1 && GSM_EXTENDED.indexOf(ch) === -1) {
      return false;
    }
  }
  return true;
}

/** GSM-7 length counting extension characters as 2 code units. */
function gsm7Length(text: string): number {
  let len = 0;
  for (const ch of text) {
    len += GSM_EXTENDED.indexOf(ch) !== -1 ? 2 : 1;
  }
  return len;
}

export function computeSmsSegments(body: string): SmsSegmentInfo {
  const text = body ?? "";
  const gsm = isGsm7(text);
  const encoding: SmsEncoding = gsm ? "GSM-7" : "UCS-2";

  // UCS-2 counts UTF-16 code units (so a surrogate-pair emoji is 2).
  const length = gsm ? gsm7Length(text) : text.length;

  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;

  let segments: number;
  let perSegment: number;
  if (length === 0) {
    segments = 0;
    perSegment = single;
  } else if (length <= single) {
    segments = 1;
    perSegment = single;
  } else {
    segments = Math.ceil(length / multi);
    perSegment = multi;
  }

  const remainingInSegment =
    segments <= 1 ? single - length : segments * perSegment - length;

  return { length, segments, encoding, remainingInSegment };
}
